import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  ControlledStringArrayFilterControl,
  ControlledStringArrayFilterSummary,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { dateSchema } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useState } from "react";
import { RiBuilding2Line, RiCalendarLine, RiFilter3Line } from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AdminEventRowFragment,
  EventsPageFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/events")({
  head: () => opsHead("Events"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminEventRowFragment>[] = [
  {
    id: "createdAt",
    enableSorting: false,
    accessorFn: (row) => row.date,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      return (
        <Tooltip>
          <TooltipTrigger className="text-left">
            <Txt variant="body" nowrap>
              <DateUI date={row.original.date} />
            </Txt>
          </TooltipTrigger>
          <TooltipContent className="bg-white font-mono text-primary">
            {row.original.date.toISOString()}
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: "kind",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Kind" />
    ),
    accessorFn: (row) => row.kind,
  },
  {
    id: "desciption",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    accessorFn: (row) => row.description,
  },
  {
    id: "organisationId",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    accessorFn: (row) => row.organisation?.name ?? "-",
  },
  {
    id: "object",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Object" />
    ),
    cell({ row }) {
      if (!row.original.object) {
        return "-";
      }
      switch (row.original.object.__typename) {
        case "UnknownEventObject":
          return (
            <p className="font-mono text-secondary text-xs">
              {row.original.object.objectKind}:{row.original.object.id}
            </p>
          );
        default:
          return (
            <p className="font-mono text-secondary text-xs">
              {row.original.object.__typename}:{row.original.object.id}
            </p>
          );
      }
    },
  },
];

function View() {
  const { useSearchParams } = useRouter();

  const [filterAndSortState, setFilterAndSortOrder] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        ids: z.array(z.string()).nullish(),
        organisationIds: z.array(z.string()).nullish(),
        kinds: z.array(z.string()).nullish(),
        desciption: z.string().nullish(),
        objectIds: z.array(z.string()).nullish(),
        startDateInclusive: dateSchema.nullish(),
        endDateExclusive: dateSchema.nullish(),
      }),
    });

  const { ...filter } = filterAndSortState;

  const baseInput = {
    first: 20,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    graphql(`
    query AdminEvents(
      $after: String,
      $first: Int!,
      $filter: EventsPageFilter!,
    ) {
      events(
          after: $after,
          first: $first,
          filter: $filter,
      ) {
        pageInfo {
            endCursor
            hasNextPage
        }
        events {
            ...AdminEventRow
        }
      }
    }

    fragment AdminEventRow on Event {
        id
        date
        organisation {
            id
            name
        }
        description
        kind
        object {
            ...on Node {
                __typename
                id
            }
            ...on UnknownEventObject {
                objectKind
            }
        }
    }
  `),
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const events = data?.events?.events ?? [];
  const nextCursor = data?.events?.pageInfo.hasNextPage
    ? (data.events.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useQuery(
    graphql(`
    query AdminEventsPage {
      organisations {
        id
        name
      }
    }
    `),
  );

  const organisationOptions =
    [...(pageData?.organisations ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((o) => ({
        value: o.id,
        label: <div>{o.name}</div>,
      })) ?? [];

  return (
    <AdminPageContainer testId="admin-payouts">
      <AdminPageHeader title="Events" />
      <div>
        <DataTable
          data={events}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination={true}
          manualSorting={true}
          hidePagination={true}
          pageSize={10_000}
          linkTo={(row) => `/events/${row.original.id}`}
          toolbarComponent={() => (
            <FilterBar
              filterValue={filterAndSortState}
              organisationOptions={organisationOptions}
              onFilterChange={setFilterAndSortOrder}
            />
          )}
          className="overflow-hidden border"
          columnSortProps={[
            {
              id: "createdAt",
              desc: true,
            },
          ]}
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!events.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: EventsPageFilter;
  onFilterChange: (_: EventsPageFilter) => void;
  organisationOptions: {
    value: string;
    label: ReactNode;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");
  let filterCount = 0;
  if (!!filterValue?.organisationIds?.length) {
    filterCount++;
  }
  if (filterValue?.startDateInclusive ?? filterValue?.endDateExclusive) {
    filterCount++;
  }
  if (!!filterValue?.kinds?.length) {
    filterCount++;
  }

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      organisationIds: undefined,
      startDateInclusive: undefined,
      endDateExclusive: undefined,
      kinds: undefined,
    });
  };

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0 " align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateExclusive ||
                  filterValue.startDateInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisation")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("kind")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Kind</div>
                {(filterValue.kinds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateInclusive: value?.startAtInclusive,
                    endDateExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive: filterValue.startDateInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateExclusive ?? undefined,
                }}
              />
            )}
            {path === "organisation" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "kind" && (
              <ControlledStringArrayFilterControl
                filterValue={filterValue.kinds ?? undefined}
                onChange={(kinds) => {
                  onFilterChange({
                    ...filterValue,
                    kinds,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>
      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateInclusive ?? undefined,
          endAtExclusive: filterValue.endDateExclusive ?? undefined,
        }}
      />
      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisation");
          setOpen(true);
        }}
      />
      <ControlledStringArrayFilterSummary
        title="Kind"
        value={filterValue.kinds ?? undefined}
        onClick={() => {
          setPath("kind");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
